import { abilityMod } from './formulas';

// Per-weapon attack + damage line for the Live Preview Card.
//
// Rules implemented:
//   - Melee weapons use Str.
//   - Ranged weapons use Dex.
//   - Finesse weapons use the BETTER of Str/Dex (this covers daggers
//     including their thrown attacks — same ability either way).
//   - Thrown handling: the SRD encodes thrown melee weapons (javelin, spear,
//     handaxe) with `weaponRange: 'melee'`, so the melee→Str branch lands
//     them correctly without a `thrown`-specific check. If a future weapon
//     is authored with `weaponRange: 'ranged'` + thrown, this code would
//     pick Dex — flagged for Phase 3 review.
//   - Proficiency bonus applies (Phase 2 assumption: starter equipment is
//     always class-proficient; non-proficient warning lands in Phase 3+).
//
// Returns an array of:
//   {
//     id, name, kind: 'melee'|'ranged',
//     attackBonus: number,
//     attackBreakdown: [{source, value}],
//     damage: { dice, type, total: '+N', breakdown: [...] },
//     properties: string[],
//   }
//
// Items the engine can't characterize as weapons are silently skipped.

const hasProperty = (item, prop) =>
  Array.isArray(item?.properties) && item.properties.includes(prop);

const pickAttackAbility = (weapon, abilities) => {
  const isMelee = weapon.weaponRange === 'melee';
  const isFinesse = hasProperty(weapon, 'finesse');
  const strMod = abilityMod(abilities.str || 10);
  const dexMod = abilityMod(abilities.dex || 10);

  if (isFinesse) {
    return strMod >= dexMod
      ? { ability: 'str', mod: strMod }
      : { ability: 'dex', mod: dexMod };
  }
  return isMelee ? { ability: 'str', mod: strMod } : { ability: 'dex', mod: dexMod };
};

const formatMod = (n) => (n >= 0 ? `+${n}` : `${n}`);

export const computeWeaponAttacks = ({ items, abilities, pb }) => {
  const weapons = (items || []).filter((i) => i?.kind === 'weapon');
  return weapons.map((weapon) => {
    const { ability, mod } = pickAttackAbility(weapon, abilities);
    const attackBonus = pb + mod;

    const attackBreakdown = [
      { source: 'Proficiency Bonus', value: pb },
      { source: `${ability.toUpperCase()} modifier`, value: mod },
    ];

    const damageDice = weapon.damage?.dice || '';
    const damageType = weapon.damage?.type || null;
    const damageBreakdown = [
      { source: 'Weapon dice', value: damageDice },
      { source: `${ability.toUpperCase()} modifier`, value: mod },
    ];
    const damageString = damageDice
      ? `${damageDice}${mod !== 0 ? ` ${formatMod(mod)}` : ''}${damageType ? ` ${damageType}` : ''}`
      : '—';

    return {
      id: weapon.id,
      name: weapon.name,
      kind: weapon.weaponRange === 'ranged' ? 'ranged' : 'melee',
      attackBonus,
      attackBreakdown,
      ability,
      damage: {
        dice: damageDice,
        type: damageType,
        modifier: mod,
        display: damageString,
        breakdown: damageBreakdown,
      },
      versatile: weapon.damage?.versatile || null,
      properties: weapon.properties || [],
      range: weapon.range || null,
      mastery: weapon.mastery || null,
    };
  });
};
